import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import V3_FEE_READER_ABI from 'abis/V3FeeReader.json'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { V3_FEE_READER_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import { useMemo } from 'react'
import { PositionDetails } from 'types/position'
import { unwrappedToken } from 'utils/unwrappedToken'
import { Abi, Address } from 'viem'
import { useReadContract } from 'wagmi'

export function useV3PositionFees({
  position,
  token0,
  token1,
  asWETH = false,
}: {
  position?: PositionDetails
  token0?: Token
  token1?: Token
  asWETH?: boolean
}): [CurrencyAmount<Currency>, CurrencyAmount<Currency>] | [undefined, undefined] {
  const { chainId } = useConnection()

  const isClosedPosition = useMemo(() => {
    if (!position?.liquidity) return true
    const liquidity = BigInt(position.liquidity)
    return liquidity === 0n
  }, [position?.liquidity])

  const zeroFees = useMemo(() => {
    if (!token0 || !token1) return [undefined, undefined] as const

    return [
      CurrencyAmount.fromRawAmount(asWETH ? token0 : unwrappedToken(token0), '0'),
      CurrencyAmount.fromRawAmount(asWETH ? token1 : unwrappedToken(token1), '0'),
    ] as const
  }, [token0, token1, asWETH])

  const feeReaderAddress = useMemo(() => {
    return chainId ? V3_FEE_READER_ADDRESSES[chainId] : undefined
  }, [chainId])

  const {
    data: result,
    isLoading,
    error,
  } = useReadContract({
    address: feeReaderAddress,
    abi: V3_FEE_READER_ABI as Abi,
    functionName: 'getFeesWithPool',
    args:
      !isClosedPosition && position?.tokenId && position?.pool?.id
        ? [BigInt(position.tokenId), position.pool.id as Address]
        : undefined,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: !isClosedPosition && !!position?.tokenId && !!position?.pool?.id && !!feeReaderAddress,
    },
  })

  return useMemo(() => {
    if (isClosedPosition) {
      return [...zeroFees] as [undefined, undefined] | [CurrencyAmount<Currency>, CurrencyAmount<Currency>]
    }

    if (!token0 || !token1 || isLoading || error || !result) {
      return [undefined, undefined]
    }

    const [amount0, amount1] = result as [bigint, bigint]

    return [
      CurrencyAmount.fromRawAmount(asWETH ? token0 : unwrappedToken(token0), amount0.toString()),
      CurrencyAmount.fromRawAmount(asWETH ? token1 : unwrappedToken(token1), amount1.toString()),
    ]
  }, [isClosedPosition, token0, token1, isLoading, error, result, asWETH, zeroFees])
}
